import {
  AskAdminToBuyCreditsNotice,
  AskAdminToSubscribeNotice,
} from "@/components/billing/ask-admin-to-subscribe-notice";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useUpgradeModal } from "@/hooks/use-upgrade-modal";
import { trackEvent } from "@/lib/analytics";
import { useIsAdmin } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Check, Loader2, Sparkles, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CreditData {
  balance: number;
  packages: Array<{
    id: string;
    price: number;
    credits: number;
    bonus: number;
    label: string;
  }>;
}

interface BillingStatus {
  tier: string;
  subscriptionStatus: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  maxEntities: string;
  maxUsers: string;
  planChosen: boolean;
  stripeConfigured: boolean;
}

const GROWTH_FEATURE_KEYS = [
  "billing.features.automations",
  "billing.features.multiEntity",
  "billing.features.reconciliation",
  "billing.features.apMatching",
  "billing.features.communications",
];

const SCALE_FEATURE_KEYS = [
  "billing.features.everythingInGrowth",
  "billing.features.financing",
  "billing.features.contractToCash",
  "billing.features.arInstruments",
  "billing.features.contractInvoicing",
  "billing.features.externalApi",
];

function TierUpgradeContent() {
  const { t } = useTranslation("common");
  const { toast } = useToast();
  // OCT-22452 / ILLEGAL E165 - checkout and credit checkout are admin-only
  // (`withAdmin`), so a non-admin sees the plans and balance but no purchase.
  const isAdmin = useIsAdmin();
  const { state } = useUpgradeModal();

  const { data: billing, isLoading } = useQuery<BillingStatus>({
    queryKey: ["/api/billing/status"],
  });

  const startUpgrade = useMutation({
    mutationFn: async (tier: "growth" | "scale") => {
      const res = await apiRequest("POST", "/api/billing/checkout", { tier });
      return res.json().catch(() => ({}));
    },
    onSuccess: (data, tier) => {
      trackEvent("Upgrade Started", { tier, source: "modal" });
      if (data.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({
        title: t("auth.error"),
        description: err.message || t("billing.checkoutFailed"),
        variant: "destructive",
      });
    },
  });

  const currentTier = billing?.tier || "starter";
  const showGrowth = currentTier === "starter";
  const showScale = currentTier === "starter" || currentTier === "growth";
  const suggestedTier = state.targetTier?.toLowerCase();

  if (isLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="upgrade-tier-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!billing?.stripeConfigured) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="text-stripe-not-configured">
        {t("billing.notConfigured")}
      </p>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2" data-testid="upgrade-tier-options">
        {showGrowth && (
          <div
            className={`flex flex-col rounded-lg border p-4 gap-3 ${
              suggestedTier === "growth" ? "border-foreground ring-1 ring-foreground" : "border-border"
            }`}
            data-testid="plan-card-growth"
          >
            <div>
              <h4 className="font-semibold">{t("billing.planGrowth")}</h4>
              <p className="text-lg font-semibold">{t("billing.priceGrowth")}</p>
            </div>
            <ul className="space-y-1.5 flex-1">
              {GROWTH_FEATURE_KEYS.map((key) => (
                <li key={key} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Check className="h-3 w-3 shrink-0" />
                  {t(key)}
                </li>
              ))}
            </ul>
            {isAdmin && (
              <Button
                size="sm"
                onClick={() => startUpgrade.mutate("growth")}
                disabled={startUpgrade.isPending}
                data-testid="button-modal-upgrade-growth"
              >
                {startUpgrade.isPending
                  ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  : <ArrowUpRight className="mr-1.5 h-3 w-3" />}
                {t("billing.upgradeToGrowth")}
              </Button>
            )}
          </div>
        )}
        {showScale && (
          <div
            className={`flex flex-col rounded-lg border p-4 gap-3 ${
              suggestedTier === "scale" ? "border-foreground ring-1 ring-foreground" : "border-border"
            }`}
            data-testid="plan-card-scale"
          >
            <div>
              <h4 className="font-semibold">{t("billing.planScale")}</h4>
              <p className="text-lg font-semibold">{t("billing.priceScale")}</p>
            </div>
            <ul className="space-y-1.5 flex-1">
              {SCALE_FEATURE_KEYS.map((key) => (
                <li key={key} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Check className="h-3 w-3 shrink-0" />
                  {t(key)}
                </li>
              ))}
            </ul>
            {isAdmin && (
              <Button
                size="sm"
                variant={suggestedTier === "scale" ? "default" : "outline"}
                onClick={() => startUpgrade.mutate("scale")}
                disabled={startUpgrade.isPending}
                data-testid="button-modal-upgrade-scale"
              >
                {startUpgrade.isPending
                  ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  : <ArrowUpRight className="mr-1.5 h-3 w-3" />}
                {t("billing.upgradeToScale")}
              </Button>
            )}
          </div>
        )}
      </div>
      {!isAdmin && <AskAdminToSubscribeNotice className="mt-4" />}
    </>
  );
}

function CreditTopUpContent() {
  const { t } = useTranslation("common");
  const { toast } = useToast();
  // OCT-22452 / ILLEGAL E165 - checkout and credit checkout are admin-only
  // (`withAdmin`), so a non-admin sees the plans and balance but no purchase.
  const isAdmin = useIsAdmin();

  const { data: billing, isLoading: billingLoading } = useQuery<BillingStatus>({
    queryKey: ["/api/billing/status"],
  });

  const { data: creditData, isLoading: creditsLoading } = useQuery<CreditData>({
    queryKey: ["/api/billing/credits"],
  });

  const buyCredits = useMutation({
    mutationFn: async (packageId: string) => {
      const res = await apiRequest("POST", "/api/billing/credits/checkout", { packageId });
      return res.json().catch(() => ({}));
    },
    onSuccess: (data) => {
      trackEvent("Credit Purchase Started", { source: "modal" });
      if (data.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({
        title: t("auth.error"),
        description: err.message || t("billing.creditPurchaseFailed"),
        variant: "destructive",
      });
    },
  });

  if (billingLoading || creditsLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="upgrade-credit-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!billing?.stripeConfigured) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="text-stripe-not-configured">
        {t("billing.notConfigured")}
      </p>
    );
  }

  return (
    <div data-testid="upgrade-credit-options">
      {creditData && (
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-sm text-muted-foreground">{t("billing.currentBalance")}</span>
          <span className="text-lg font-semibold" data-testid="text-modal-credit-balance">
            ${(creditData.balance ?? 0).toFixed(2)}
          </span>
        </div>
      )}
      {!isAdmin && <AskAdminToBuyCreditsNotice className="mt-4" />}
      {isAdmin && (
        <div className="grid grid-cols-2 gap-2">
          {(creditData?.packages || []).map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => buyCredits.mutate(pkg.id)}
              disabled={buyCredits.isPending}
              className="relative flex flex-col items-center gap-1 p-3 rounded-lg border border-border hover:border-foreground/30 hover:bg-muted/30 transition-all text-center"
              data-testid={`button-modal-buy-${pkg.id}`}
            >
              {pkg.bonus > 0 && (
                <span className="absolute -top-2 right-1 text-[9px] font-mono uppercase tracking-wider bg-foreground text-background px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                  <Zap className="h-2.5 w-2.5" />
                  +${pkg.bonus}
                </span>
              )}
              <span className="text-lg font-semibold">{pkg.label}</span>
              <span className="text-[10px] text-muted-foreground">
                {t("billing.creditsAmount", { amount: pkg.credits })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function UpgradeModal() {
  const { t } = useTranslation("common");
  const { state, closeUpgradeModal } = useUpgradeModal();

  const isTierUpgrade = state.reason === "tier-upgrade";
  const title = isTierUpgrade
    ? t("billing.upgradeYourPlan", { defaultValue: "Upgrade Your Plan" })
    : t("billing.topUpCredits", { defaultValue: "Top Up AI Credits" });

  const defaultDescription = isTierUpgrade
    ? t("billing.upgradeDescription", { defaultValue: "Choose a plan to unlock more features and higher limits." })
    : t("billing.topUpDescription", { defaultValue: "Purchase additional AI credits to continue using AI features." });

  return (
    <Dialog open={state.open} onOpenChange={(open) => !open && closeUpgradeModal()}>
      <DialogContent className="sm:max-w-xl" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="text-upgrade-modal-title">
            {isTierUpgrade ? <ArrowUpRight className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            {title}
          </DialogTitle>
          <DialogDescription data-testid="text-upgrade-modal-description">
            {state.message || defaultDescription}
          </DialogDescription>
        </DialogHeader>
        {isTierUpgrade ? <TierUpgradeContent /> : <CreditTopUpContent />}
      </DialogContent>
    </Dialog>
  );
}
